'use client';

/**
 * useToast Hook
 *
 * Convenient hook for displaying toast notifications.
 * Provides shorthand methods for common toast variants.
 */
import { useCallback } from 'react';

import type { ToastItem, ToastPosition } from './ToastProvider';
import { useToastContext } from './ToastProvider';

export interface ToastOptions {
  title?: string;
  description?: string;
  duration?: number;
  closable?: boolean;
  position?: ToastPosition;
  icon?: React.ReactNode;
}

export interface UseToastReturn {
  /**
   * Display a toast with custom configuration
   */
  toast: (options: ToastOptions & { variant?: ToastItem['variant'] }) => string;
  /**
   * Display a success toast
   */
  success: (titleOrOptions: string | ToastOptions, description?: string) => string;
  /**
   * Display an error toast
   */
  error: (titleOrOptions: string | ToastOptions, description?: string) => string;
  /**
   * Display a warning toast
   */
  warning: (titleOrOptions: string | ToastOptions, description?: string) => string;
  /**
   * Display an info toast
   */
  info: (titleOrOptions: string | ToastOptions, description?: string) => string;
  /**
   * Display a location toast (for location-related notifications)
   */
  location: (titleOrOptions: string | ToastOptions, description?: string) => string;
  /**
   * Remove a specific toast by ID
   */
  dismiss: (id: string) => void;
  /**
   * Remove all toasts
   */
  dismissAll: () => void;
}

function normalizeOptions(titleOrOptions: string | ToastOptions, description?: string): ToastOptions {
  if (typeof titleOrOptions === 'string') {
    return {
      title: titleOrOptions,
      description
    };
  }
  return titleOrOptions;
}

export function useToast(): UseToastReturn {
  const { addToast, removeToast, clearAllToasts } = useToastContext();

  const toast = useCallback(
    (options: ToastOptions & { variant?: ToastItem['variant'] }) => {
      return addToast({
        variant: options.variant || 'info',
        title: options.title,
        description: options.description,
        duration: options.duration ?? 5000,
        closable: options.closable ?? true,
        position: options.position,
        icon: options.icon
      });
    },
    [addToast]
  );

  const success = useCallback(
    (titleOrOptions: string | ToastOptions, description?: string) => {
      const options = normalizeOptions(titleOrOptions, description);
      return toast({ ...options, variant: 'success' });
    },
    [toast]
  );

  const error = useCallback(
    (titleOrOptions: string | ToastOptions, description?: string) => {
      const options = normalizeOptions(titleOrOptions, description);
      return toast({ ...options, variant: 'error' });
    },
    [toast]
  );

  const warning = useCallback(
    (titleOrOptions: string | ToastOptions, description?: string) => {
      const options = normalizeOptions(titleOrOptions, description);
      return toast({ ...options, variant: 'warning' });
    },
    [toast]
  );

  const info = useCallback(
    (titleOrOptions: string | ToastOptions, description?: string) => {
      const options = normalizeOptions(titleOrOptions, description);
      return toast({ ...options, variant: 'info' });
    },
    [toast]
  );

  const location = useCallback(
    (titleOrOptions: string | ToastOptions, description?: string) => {
      const options = normalizeOptions(titleOrOptions, description);
      return toast({
        ...options,
        variant: 'location',
        position: options.position || 'bottom-center',
        duration: options.duration || 0, // Default to persistent for location toasts
        closable: options.closable !== false // Default to closable
      });
    },
    [toast]
  );

  const dismiss = useCallback(
    (id: string) => {
      removeToast(id);
    },
    [removeToast]
  );

  const dismissAll = useCallback(() => {
    clearAllToasts();
  }, [clearAllToasts]);

  return {
    toast,
    success,
    error,
    warning,
    info,
    location,
    dismiss,
    dismissAll
  };
}
